// Lightweight dataLayer wrapper. Mirrors waitlist.ts's local helper so every
// part of the app can fire events through one channel (GTM/GA4/Segment all
// listen to window.dataLayer).
type DataLayerWindow = Window & { dataLayer?: Array<Record<string, unknown>> };

export function track(event: string, payload: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event, ts: Date.now(), ...payload });
}

/** Fired whenever the visitor picks (or changes) their state. */
export function trackStateSelected(opts: {
  code: string;
  name?: string;
  placement: string;
  previous?: string;
}) {
  track("state_selected", {
    state: opts.code,
    state_name: opts.name,
    is_pr: opts.code === "PR",
    placement: opts.placement,
    previous_state: opts.previous || null,
  });
}

/**
 * Classify how the visitor arrived at the current page so we can attribute
 * conversions back to entry path. Returns one of:
 *   - "campaign:<utm_source>" — paid/owned campaign with UTM
 *   - "internal_nav"          — clicked from somewhere else on olivehealth.care
 *   - "search:<host>"         — referred by Google / Bing / DDG / etc.
 *   - "social:<host>"         — referred by a known social network
 *   - "external_referral"     — any other off-site referrer
 *   - "direct"                — no referrer (typed URL, bookmark, app link)
 */
export function classifyEntryPath(): string {
  if (typeof window === "undefined") return "direct";
  const url = new URL(window.location.href);
  const utm = url.searchParams.get("utm_source");
  if (utm) return `campaign:${utm.toLowerCase()}`;

  const ref = document.referrer;
  if (!ref) return "direct";
  let refHost = "";
  try { refHost = new URL(ref).hostname.replace(/^www\./, ""); } catch { return "direct"; }

  if (refHost === window.location.hostname.replace(/^www\./, "")) return "internal_nav";

  const search = ["google.", "bing.com", "duckduckgo.com", "yahoo.", "ecosia.org", "brave.com"];
  if (search.some((s) => refHost.includes(s))) return `search:${refHost}`;

  const social = ["facebook.com", "instagram.com", "x.com", "twitter.com", "t.co", "linkedin.com", "lnkd.in", "tiktok.com", "reddit.com", "youtube.com", "pinterest.com"];
  if (social.some((s) => refHost === s || refHost.endsWith(`.${s}`))) return `social:${refHost}`;

  return "external_referral";
}

/** Fired once when a visitor lands on a /states/:slug page. */
export function trackStatePageView(opts: { code: string; name?: string; slug?: string }) {
  track("state_page_view", {
    state: opts.code,
    state_name: opts.name,
    state_slug: opts.slug,
    is_pr: opts.code === "PR",
    placement: classifyEntryPath(),
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    path: typeof window !== "undefined" ? window.location.pathname : null,
  });
}
